// Create-blueprint page. Intentionally minimal: just a Name. The user can
// flesh out prompts, target artifacts, and skills repo URL on the detail
// page after creation. This matches the simplified backend (zero-prompt
// blueprints are valid stubs) and keeps the create flow snappy — three
// keystrokes and they're on the editor.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { BackButton } from '../../../components/common/BackButton';
import { PageHeader } from '../../../components/common/PageHeader';
import { routes } from '../../../lib/constants';
import { useMetaphysics } from '../../../lib/metaphysics';
import { guardSuperuser } from '../index';

export const Route = createFileRoute('/internal/blueprints/new')({
  beforeLoad: () => guardSuperuser(),
  component: NewBlueprintPage,
});

function NewBlueprintPage() {
  const mp = useMetaphysics();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [name, setName] = useState('');

  const create = useMutation({
    mutationFn: () =>
      mp.createBlueprint({
        name: name.trim(),
        prompts: [],
        targetArtifacts: [],
        skillsRepoUrl: null,
      }),
    onSuccess: async (b) => {
      try {
        await qc.invalidateQueries({ queryKey: ['mp', 'blueprints'] });
      } catch (err) {
        console.error(err);
      }
      try {
        await nav({ to: routes.internalBlueprintDetail(b.id) });
      } catch (err) {
        console.error(err);
      }
    },
  });

  const canSubmit = name.trim().length > 0 && !create.isPending;

  return (
    <>
      <PageHeader
        title="Create blueprint"
        actions={<BackButton to={routes.INTERNAL_BLUEPRINTS} label="Blueprints" />}
      />
      <div className="card" style={{ maxWidth: 560 }}>
        <div className="card-body">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit) create.mutate();
            }}
            style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}
          >
            <label className="field">
              <span className="field-label">Name</span>
              <input
                className="input"
                required={true}
                placeholder="my-blueprint"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus={true}
              />
            </label>
            {create.error ? (
              <div className="alert alert--danger">{(create.error as Error).message}</div>
            ) : null}
            <div style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end' }}>
              <Link to={routes.INTERNAL_BLUEPRINTS} className="btn btn--ghost btn--sm">
                Cancel
              </Link>
              <button type="submit" className="btn btn--primary" disabled={!canSubmit}>
                {create.isPending ? 'Creating…' : 'Create blueprint'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
